import React, { useState, useEffect, useContext } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';

const PersonalInfoTab = () => {
    const { user, logout } = useContext(AuthContext);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    // Changing password fields
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const { showAlert, showConfirm } = useModal();

    useEffect(() => {
        const fetchUserData = async () => {
            // Retrieve ID from user object (support both id and sub just in case, but prefer id)
            const userId = user?.id || user?.sub;
            if (!userId) {
                setLoading(false);
                return;
            }

            try {
                const response = await api.get(`/users/${userId}`);
                const userData = response.data;
                setName(userData.name);
                setEmail(userData.email);
            } catch (err) {
                console.error('Failed to fetch user:', err);
                setError('사용자 정보를 불러오는데 실패했습니다.');
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchUserData();
        } else {
            // Should not happen if protected, but good for safety
            setLoading(false);
        }
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        if (newPassword && newPassword !== confirmPassword) {
            setError('새 비밀번호가 일치하지 않습니다.');
            setIsSubmitting(false);
            return;
        }

        try {
            const data = {
                // Name and Email are NOT sent for update to prevent changing them
                // name, 
                // email, 
                ...(newPassword ? { password: newPassword } : {})
            };

            if (Object.keys(data).length === 0) {
                setIsSubmitting(false);
                return;
            }

            await api.patch(`/users/${user?.id || user?.sub}`, data);
            showAlert('성공', '비밀번호가 성공적으로 변경되었습니다.');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error(err);
            if (err.response?.data?.message) {
                setError(`수정 실패: ${err.response.data.message}`);
            } else {
                setError('정보 수정 중 오류가 발생했습니다.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
    }

    return (
        <div className="personal-info-tab">
            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleSubmit} className="bg-white p-4 rounded-4 border">
                <h6 className="fw-bold mb-4 text-primary">기본 정보</h6>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">이름</Form.Label>
                    <Form.Control
                        type="text"
                        value={name}
                        readOnly
                        disabled
                        className="py-2 bg-light border-0"
                    />
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">이메일</Form.Label>
                    <Form.Control
                        type="email"
                        value={email}
                        readOnly
                        disabled
                        className="py-2 bg-light border-0"
                    />
                </Form.Group>

                <hr className="my-5" />

                <h6 className="fw-bold mb-3 text-primary">비밀번호 변경</h6>
                <div className="bg-light p-4 rounded-3 mb-3">
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-muted">새 비밀번호</Form.Label>
                        <Form.Control
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="변경할 경우에만 입력하세요"
                            className="py-2"
                        />
                    </Form.Group>

                    <Form.Group className="mb-0">
                        <Form.Label className="fw-bold small text-muted">새 비밀번호 확인</Form.Label>
                        <Form.Control
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="변경할 경우에만 입력하세요"
                            className="py-2"
                        />
                    </Form.Group>
                </div>

                <div className="d-flex justify-content-end mt-4">
                    <Button
                        variant="primary"
                        type="submit"
                        className="px-4 py-2 fw-bold"
                        disabled={isSubmitting || !newPassword}
                    >
                        {isSubmitting ? '저장 중...' : '비밀번호 변경 저장'}
                    </Button>
                </div>
            </Form>

            <div className="mt-5 text-end">
                <Button
                    variant="link"
                    className="text-muted small text-decoration-none p-0"
                    onClick={async () => {
                        const confirmed = await showConfirm(
                            '회원 탈퇴 (계정 비활성화)',
                            '정말로 탈퇴하시겠습니까? 탈퇴 시 관리자 승인 전까지 계정이 비활성화되며 로그인이 차단됩니다.\n작성된 점검 리포트 및 이슈 데이터는 보존됩니다.',
                            'danger'
                        );

                        if (confirmed) {
                            try {
                                setIsSubmitting(true);
                                await api.patch(`/users/${user?.id || user?.sub}/deactivate`);

                                // Remove token and update state
                                logout();
                                window.location.href = '/login';
                            } catch (err) {
                                console.error('Failed to deactivate account:', err);
                                showAlert('오류', '계정 탈퇴 중 문제가 발생했습니다. 관리자에게 문의하세요.', 'danger');
                                setIsSubmitting(false);
                            }
                        }
                    }}
                    disabled={isSubmitting}
                >
                    회원 탈퇴
                </Button>
            </div>
        </div>
    );
};

export default PersonalInfoTab;
